import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { Button, Input, Stack, Modal, useModals, ModalProps } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  ApiJobArchivingReason,
  ApiJobArchivingReasonPayload,
} from "@/types/recruit/job-archiving-reason.types.ts";
import {
  SaveParams,
  useJobArchivingReasonSave,
} from "@/queries/recruit/use-job-archiving-reasons.query.ts";

export interface JobArchivingReasonEditModelProps extends ModalProps {
  jobArchivingReason?: ApiJobArchivingReason;
}

export function JobArchivingReasonEditModal({
  jobArchivingReason,
  ...props
}: JobArchivingReasonEditModelProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = { ...props };

  const JobArchivingReasonSchemaObject = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ApiJobArchivingReasonPayload>({
    mode: "onSubmit",
    resolver: zodResolver(JobArchivingReasonSchemaObject),
    defaultValues: {
      title: jobArchivingReason?.title ?? "",
    },
  });

  const jobArchivingReasonSave = useJobArchivingReasonSave(control);

  const onSubmit = handleSubmit(async (data) => {
    const payload: SaveParams = {
      id: jobArchivingReason?.id,
      title: data.title,
    };

    try {
      await jobArchivingReasonSave.mutateAsync(payload, {
        onSuccess: (data) => {
          if (jobArchivingReason) {
            analyticsService.trackEvent(analyticEvents.jobArchivingReasons.edited, {
              [analyticProperties.id]: jobArchivingReason.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.jobArchivingReasons.created, {
              [analyticProperties.id]: data.id,
            });
          }

          close(id);
        },
      });
    } catch {}
  });

  return (
    <Modal
      {...props}
      title={t("ats:job_archiving_reason")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={jobArchivingReasonSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("common:title")}
          required={true}
          type={"text"}
          autoFocus={true}
          {...register("title")}
          error={errors.title?.message}
        />
      </form>
    </Modal>
  );
}
